import {
    ReactNode,
    SyntheticEvent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import {
    LoadingSpinnerPresets,
    PresetLoadingSpinner,
} from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner';

export function SelectableList<T extends number | string>({
    selectionItems,
    onChange,
    initialSelection,
    multiSelect,
    preventDeselect,
    loading,
    autoselectUnique,
}: {
    selectionItems: SelectableListItem<T>[];
    onChange?: (selectedIds: T[]) => void;
    initialSelection?: T[];
    multiSelect?: boolean;
    preventDeselect?: boolean;
    loading?: boolean;
    autoselectUnique?: boolean;
}) {
    const [selectedIds, setSelectedIds] = useState<T[]>(initialSelection ?? []);

    const updateSelection = useCallback(
        (newSelection: T[]) => {
            setSelectedIds(newSelection);
            onChange?.(newSelection);
        },
        [onChange],
    );

    const handleSelectionChange = useCallback(
        (clickedId: T) => {
            if (!loading) {
                if (selectedIds.includes(clickedId)) {
                    updateSelection(selectedIds.filter(id => id !== clickedId));
                } else if (multiSelect) {
                    updateSelection([...selectedIds, clickedId]);
                } else {
                    updateSelection([clickedId]);
                }
            }
        },
        [loading, multiSelect, selectedIds, updateSelection],
    );

    useEffect(() => {
        if (
            autoselectUnique &&
            !initialSelection &&
            selectionItems.length === 1
        ) {
            const timeoutId = setTimeout(
                () => handleSelectionChange(selectionItems[0].id),
                0,
            );
            return () => clearTimeout(timeoutId);
        }
    }, [
        autoselectUnique,
        handleSelectionChange,
        initialSelection,
        selectionItems,
    ]);

    return (
        <Stack
            direction="column"
            spacing={2}
            component="ul"
            sx={{
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                listStyleType: 'none',
                margin: 0,
                padding: 0,
            }}
        >
            {selectionItems.map(item => (
                <SelectionItem
                    key={item.id}
                    item={item}
                    selected={selectedIds.includes(item.id)}
                    loading={selectedIds.includes(item.id) && Boolean(loading)}
                    disabled={Boolean(loading)}
                    onClick={() => handleSelectionChange(item.id)}
                    preventDeselect={preventDeselect}
                />
            ))}
        </Stack>
    );
}

interface SelectableListItem<T> {
    id: T;
    title?: ReactNode;
    content: ReactNode;
}

function SelectionItem<T>({
    item,
    selected,
    loading,
    disabled,
    onClick,
    preventDeselect,
}: {
    item: SelectableListItem<T>;
    selected: boolean;
    loading: boolean;
    disabled: boolean;
    onClick: (e: SyntheticEvent) => void;
    preventDeselect?: boolean;
}) {
    function cardStyle(theme: Theme) {
        return {
            display: 'flex',
            justifyContent: 'space-between',
            itemsAlign: 'center',
            padding: theme.spacing(2),
            paddingRight: theme.spacing(3),
            minHeight: theme.spacing(9),
            backgroundColor: selected
                ? theme.palette.primary.main
                : theme.palette.primary.light,
            color: selected ? theme.palette.primary.contrastText : undefined,
        };
    }

    if ((selected && preventDeselect) || disabled) {
        return (
            <Card elevation={0} component="li" sx={cardStyle}>
                <CardContent
                    item={item}
                    selected={selected}
                    loading={loading}
                />
            </Card>
        );
    }

    return (
        <Card elevation={0} component="li">
            <CardActionArea sx={cardStyle} onClick={onClick}>
                <CardContent
                    item={item}
                    selected={selected}
                    loading={loading}
                />
            </CardActionArea>
        </Card>
    );
}

function CardContent<T>({
    item,
    selected,
    loading,
}: {
    item: SelectableListItem<T>;
    selected: boolean;
    loading: boolean;
}) {
    return (
        <>
            <Box sx={{ marginY: 'auto', flexGrow: 1 }}>
                {item.title && (
                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                        }}
                    >
                        {item.title}
                    </Typography>
                )}
                {item.content}
            </Box>
            {selected && !loading && (
                <Check sx={{ marginY: 'auto', marginLeft: 1 }} />
            )}
            {loading && (
                <PresetLoadingSpinner
                    color="inherit"
                    preset={LoadingSpinnerPresets.SelectableList}
                />
            )}
        </>
    );
}
